<template>
  <div class="flex flex-col">
    <div class="password-wrapper relative flex items-center justify-center">
      <Input
        class="w-full -my-0"
        :id="id"
        :name="name"
        :type="showPassword ? 'text' : 'password'"
        :placeholder="placeholder"
        :autofocus="autofocus"
        :model-value="modelValue"
        :error="error"
        error-message=""
        :valid="!validatePassword && valid"
        :autocomplete="autocomplete"
        :aria-label="$attrs['aria-label']"
        :aria-describedby="inputAriaDescribedBy"
        :aria-invalid="validatePassword && !isValidPassword ? 'true' : $attrs['aria-invalid']"
        :aria-required="$attrs['aria-required']"
        :required="$attrs.required"
        @update:modelValue="(value) => $emit('update:modelValue', value)"
        @focus="onInputFocus"
        @blur="onInputBlur"
      />

      <button
        type="button"
        class="absolute right-2 top-0 bottom-0 my-auto
          flex items-center justify-center
          w-8 h-8 rounded-full
          cursor-pointer border-0 bg-transparent p-0
          transition-colors"
        :class="{
        }"
        :style="{
          color: error
            ? '#D20000'
            : focused
              ? global.primaryColor
              : boxColor
        }"
        :aria-label="showPassword
          ? $t('message.accessibility.hide_password')
          : $t('message.accessibility.show_password')"
        :aria-pressed="showPassword"
        @click="onTogglePassword"
        @focus="onToggleFocus"
        @blur="onToggleBlur"
        @mousedown.prevent
      >
        <EyeIcon
          :eye-type="showPassword ? 'on' : 'off'"
          aria-hidden="true"
        />
      </button>
    </div>
    <!-- Status message for screen readers - always present but hidden -->
    <div
      v-if="validatePassword"
      id="password-status"
      class="sr-only"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ focusAnnouncementMessage }}
    </div>

    <div
      v-if="validatePassword && showPasswordPolicy && !isValidPassword"
      id="password-requirements"
      class="flex flex-col items-start text-left bg-gray-100 -mt-2 p-2 space-y-2"
      role="region"
      :aria-label="$t('message.accessibility.password_requirements')"
    >
      <p
        class="text-base"
        :style="{ color: boxColor }"
        aria-hidden="true">
        {{ $t('message.password_policy_title') }}
      </p>

      <ul :aria-label="$t('message.accessibility.password_requirements_list')">
        <li
          v-for="(item, index) in passwordRequirementsList()"
          :key="index"
          class="flex items-center space-x-1 text-gray-600"
          :aria-label="`${item.text}: ${item.validator ? $t('message.accessibility.requirement_met') : $t('message.accessibility.requirement_not_met')}`"
        >
          <CheckmarkIcon 
            :checked="item.validator" 
            aria-hidden="true" 
          />
          <p aria-hidden="true">
            {{ item.text }}
          </p>
        </li>
      </ul>
      <div
        class="w-full bg-gray-300 h-1 rounded-lg"
        role="progressbar"
        :aria-valuenow="statusBarPercentage"
        aria-valuemin="0"
        aria-valuemax="6"
        :aria-label="$t('message.accessibility.progress_label', { current: statusBarPercentage, total: 6 })"
      >
        <div
          class="h-1 rounded-lg"
          :style="{ backgroundColor: successColor }"
          :class="statusBarPercentage === 0 ? 'w-0' : `w-${statusBarPercentage}/6`"
        />
      </div>
    </div>
    <p
      v-if="error && visibleErrorMessage"
      :id="id ? `${id}-error` : undefined"
      class="input-error-message"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      {{ visibleErrorMessage }}
    </p>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Input from './Input.vue';
import CheckmarkIcon from './CheckmarkIcon.vue';
import EyeIcon from './EyeIcon.vue';
import { mapGetters } from 'vuex';

export default defineComponent({
  name: 'PasswordInput',
  components: {
    Input,
    CheckmarkIcon,
    EyeIcon
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    autocomplete: {
      type: String
    },
    id: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    isValid: {
      type: Boolean,
      default: false
    },
    valid: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    validatePassword: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'update:isValid', 'update:requirements'],
  data () {
    return {
      showPasswordPolicy: false,
      showPassword: false,
      focused: false,
      focusAnnouncementMessage: ''
    };
  },
  watch: {
    isValidPassword (isValid: boolean): void {
      if (this.validatePassword) {
        this.$emit('update:isValid', isValid);
      }
    },
    screenReaderMessage (newMessage: string): void {
      // Update the message only if the field is focused
      if (this.focused && this.validatePassword) {
        this.focusAnnouncementMessage = newMessage;
      }
      // Emit requirements for parent components
      if (this.validatePassword) {
        this.$emit('update:requirements', newMessage);
      }
    }
  },
  mounted() {
    // Emit initial requirements state
    if (this.validatePassword) {
      this.$emit('update:requirements', this.screenReaderMessage);
    }
  },
  methods: {
    onTogglePassword () {
      this.showPassword = !this.showPassword;
    },
    onInputFocus () {
      this.focused = true;
      this.showPasswordPolicy = true;
      if (this.validatePassword) {
        this.focusAnnouncementMessage = this.screenReaderMessage;
      }
    },
    onInputBlur (event: FocusEvent) {
      if (this.isFocusInsideComponent(event)) {
        return;
      }
      this.focused = false;
      if (this.isValidPassword) {
        this.showPasswordPolicy = false;
      }
      this.focusAnnouncementMessage = '';
    },
    onToggleFocus () {
      this.focused = true;
    },
    onToggleBlur (event: FocusEvent) {
      if (this.isFocusInsideComponent(event)) {
        return;
      }
      this.focused = false;
      if (this.isValidPassword) {
        this.showPasswordPolicy = false;
      }
    },
    isFocusInsideComponent (event: FocusEvent): boolean {
      const next = event.relatedTarget as HTMLElement | null;
      return !!next && (this.$el as HTMLElement).contains(next);
    },
    passwordRequirementsList () {
      return [
        {
          text: this.$t('message.password_policy_spaces'),
          validator: this.passwordMustNotStartOrEndWithSpaces
        },
        {
          text: this.$t('message.password_policy_length'),
          validator: this.passwordHasValidLenght
        },
        {
          text: this.$t('message.password_policy_lowercase'),
          validator: this.passwordHasLowercase
        },
        {
          text: this.$t('message.password_policy_uppercase'),
          validator: this.passwordHasUppercase
        },
        {
          text: this.$t('message.password_policy_number'),
          validator: this.passwordHasNumber
        },
        {
          text: this.$t('message.password_policy_special'),
          validator: this.passwordHasSpecialChar
        }
      ];
    }

  },
  computed: {
    ...mapGetters([
      'passwordPolicy',
      'global'
    ]),
    isValidPassword (): boolean {
      return this.passwordMustNotStartOrEndWithSpaces &&
        this.passwordHasValidLenght &&
        this.passwordHasLowercase &&
        this.passwordHasUppercase &&
        this.passwordHasNumber &&
        this.passwordHasSpecialChar;
    },
    boxColor (): string {
      return this.global.boxColor || '#696E79';
    },
    successColor (): string {
      return this.global.successColor || '#337E33';
    },
    screenReaderMessage (): string {
      if (this.isValidPassword) {
        return this.$t('message.accessibility.password_valid');
      }

      const statusList = this.passwordRequirementsList()
        .map((req: any) => {
          const status = req.validator
            ? this.$t('message.accessibility.requirement_met')
            : this.$t('message.accessibility.requirement_not_met');
          return `${req.text}: ${status}`;
        })
        .join(', ');

      return `${this.$t('message.accessibility.password_requirements')}: ${statusList}`;
    },
    statusBarPercentage (): number {
      let percentage = 0;
      if (this.passwordMustNotStartOrEndWithSpaces) {
        percentage++;
      }
      if (this.passwordHasValidLenght) {
        percentage++;
      }
      if (this.passwordHasLowercase) {
        percentage++;
      }
      if (this.passwordHasUppercase) {
        percentage++;
      }
      if (this.passwordHasNumber) {
        percentage++;
      }
      if (this.passwordHasSpecialChar) {
        percentage++;
      }
      return percentage;
    },
    inputAriaDescribedBy (): string | undefined {
      const ids: string[] = [];
      const describedBy = this.$attrs['aria-describedby'];

      if (typeof describedBy === 'string' && describedBy.trim()) {
        ids.push(describedBy.trim());
      }

      if (this.validatePassword) {
        ids.push('password-status');
      }

      if (this.error && this.errorMessage && this.id) {
        ids.push(`${this.id}-error`);
      }

      return ids.length ? ids.join(' ') : undefined;
    },
    visibleErrorMessage (): string {
      if (!this.errorMessage) {
        return '';
      }

      return this.errorMessage.startsWith('* ')
        ? this.errorMessage
        : `* ${this.errorMessage}`;
    },
    passwordMustNotStartOrEndWithSpaces (): boolean {
      return this.passwordPolicy.noStartingSpacesRgx.test(this.modelValue) &&
        this.passwordPolicy.noEndingSpacesRgx.test(this.modelValue);
    },
    passwordHasValidLenght (): boolean {
      return this.modelValue?.length >= this.passwordPolicy.length;
    },
    passwordHasLowercase (): boolean {
      return this.passwordPolicy.lowerCaseRgx.test(this.modelValue);
    },
    passwordHasUppercase (): boolean {
      return this.passwordPolicy.upperCaseRgx.test(this.modelValue);
    },
    passwordHasNumber (): boolean {
      return this.passwordPolicy.numberRgx.test(this.modelValue);
    },
    passwordHasSpecialChar (): boolean {
      return this.passwordPolicy.specialCharRgx.test(this.modelValue);
    }
  }
});
</script>

<style scoped>
.password-wrapper button:focus:not(:focus-visible) {
  outline: none;
}

.password-wrapper button:focus-visible {
  outline: 2px solid black;
  outline-offset: -2px;
  border-radius: 4px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.input-error-message {
  margin: 0.25rem 0 0 0;
  font-size: 0.75rem;
  color: #D20000;
  font-weight: 700;
  line-height: 1.25;
  text-align: left;
}
</style>
